import React, { useCallback, useEffect, useRef, useState } from "react";
import "./HeroSection.css";
import { Link } from "react-router-dom";
import Button from "@shared/Button/Button";
import HeroBadge from "@shared/HeroBadge/HeroBadge";
import ChannelRotator from "@shared/ChannelRotator/ChannelRotator";
import gmailLogo from "@assets/images/connectors/gmail.png";
import outlookLogo from "@assets/images/connectors/outlook.svg";
import slackLogo from "@assets/images/connectors/slack-36.png";
import teamsLogo from "@assets/images/connectors/microsoft-teams.svg";
import mouseArrow from "@icons/instant-mouse-arrow.svg";
import DiligenceRoomCard from "../DiligenceRoomCard/DiligenceRoomCard";
import CourtSectionCard from "../CourtSectionCard/CourtSectionCard";
import ProjectManagementCard from "../ProjectManagementCard/ProjectManagementCard";
import RevenueMetricsCard from "../RevenueMetricsCard/RevenueMetricsCard";
import CapTableCard from "../CapTableCard/CapTableCard";
import InstantCiteVerification from "../InstantCiteVerification/InstantCiteVerification";
import { EmailWorkflowAttachmentPreview } from "../EmailWorkFlow/EmailWorkFlow";
import { TbCheck, TbLock, TbPlus, TbSend } from "react-icons/tb";

// The headline stays fixed while the channel line below it rotates.
const HOME_TITLE = "Hire Vienna to work across all of your firm's knowledge & tools";
const CHANNEL_PREFIX = "over ";

// React 18's production style diff can throw in older Firefox releases when a
// host element's style changes from an object to null/undefined. Keep the
// animated pointer's style object-shaped even while it has no coordinates.
export const demoCursorStyle = (position) => position ? {
  translate: `${position.left}px ${position.top}px`,
} : {};
export const normalizeStoryboardIndex = (index, length) => {
  if (!Number.isInteger(length) || length <= 0) return 0;
  const safeIndex = Number.isFinite(index) ? Math.trunc(index) : 0;
  return ((safeIndex % length) + length) % length;
};
const CHANNEL_SUFFIX = "";
const CHANNEL_WORDS = ["email", "Slack", "Teams", "WhatsApp"];
const CHANNEL_FINAL = "wherever you already work";
const HOME_CHANNEL_INITIAL = `${CHANNEL_PREFIX}${CHANNEL_WORDS[0]}`;
const HOME_TITLE_TEXT = `${HOME_TITLE}\n${HOME_CHANNEL_INITIAL}`;
const HERO_VALUE_WORDS = ["overload", "adoption", "ROI"];

/* Static JuristAI mark (same geometry as logos/JuristAI.svg, without the float animation). */
const AgentMark = () => (
  <span className="hero-agent-mark" aria-hidden="true">
    <svg viewBox="4 5 36 35" focusable="false">
      <path d="M9.49057 39.013H22.6864C23.2223 39.0148 23.7495 38.9535 24.2682 38.829C24.7869 38.7036 25.2808 38.5195 25.7499 38.275C26.219 38.0305 26.648 37.7346 27.0371 37.3863C27.4261 37.038 27.7626 36.6492 28.0468 36.2206L38.6321 19.9966C39.6275 18.47 38.4337 16.5266 36.5001 16.5266H23.2156C22.6931 16.5239 22.1792 16.5844 21.6729 16.7062C21.1676 16.828 20.6861 17.0084 20.2284 17.2466C19.7708 17.4848 19.3522 17.7735 18.9727 18.1128C18.5932 18.452 18.2652 18.8309 17.9878 19.2496L7.35864 35.5475C6.36323 37.0687 7.55791 39.013 9.49057 39.013Z" fill="var(--color-secondary)" />
      <path d="M7.08237 27.9H20.2782C20.8141 27.9018 21.3413 27.8405 21.86 27.716C22.3787 27.5905 22.8726 27.4065 23.3417 27.162C23.8108 26.9175 24.2398 26.6215 24.6288 26.2733C25.0179 25.925 25.3544 25.5362 25.6386 25.1076L36.2239 8.88362C37.2193 7.35704 36.0255 5.41363 34.0919 5.41363H20.8074C20.2849 5.41092 19.771 5.47137 19.2647 5.59318C18.7594 5.71498 18.2779 5.89542 17.8202 6.13361C17.3625 6.3718 16.944 6.66052 16.5645 6.99976C16.185 7.339 15.857 7.71794 15.5796 8.13657L4.95043 24.4345C3.95502 25.9557 5.14971 27.9 7.08237 27.9Z" fill="#ffffff" />
    </svg>
  </span>
);

/* Every authority below resolves to a real document in the Vienna law
   library (FRCP rule text or a full opinion). No external citators — the
   whole point is that the cite lands back in our own corpus. */
const LAW = {
  rule8: {
    label: "Fed. R. Civ. P. 8",
    meta: "Pleading standard · Vienna law library",
    preview: "Rule 8(a) requires a short and plain statement of the grounds for jurisdiction and of the claim — the standard Twombly and Iqbal construe.",
    url: "https://nougat.law/law/Federal/rules/frcp/rule_8",
  },
  rule15: {
    label: "Fed. R. Civ. P. 15",
    meta: "Leave to amend · Vienna law library",
    preview: "Rule 15(a) lets a party amend once as of course within 21 days of a Rule 12(b) motion, and directs courts to freely give leave when justice so requires.",
    url: "https://nougat.law/law/Federal/rules/frcp/rule_15",
  },
  twombly: {
    label: "Bell Atlantic Corp. v. Twombly",
    meta: "550 U.S. 544 (2007) · full opinion in the Vienna law library",
    preview: "Pleadings must state a claim to relief that is plausible on its face; conclusory allegations do not survive Rule 12(b)(6).",
    url: "https://nougat.law/law/Federal/opinions/145730/bell-atlantic-corp-v-twombly",
  },
  iqbal: {
    label: "Ashcroft v. Iqbal",
    meta: "556 U.S. 662 (2009) · full opinion in the Vienna law library",
    preview: "Extends Twombly to all civil actions: courts disregard legal conclusions and ask whether well-pleaded facts plausibly show entitlement to relief.",
    url: "https://nougat.law/law/Federal/opinions/145875/ashcroft-v-iqbal",
  },
};

const RESEARCH_EMAIL = {
  subject: "Caselaw research: opposition authorities checked",
  preview: "7 cited authorities verified; 3 stronger authorities linked for review.",
  time: "8:32 AM",
  chip: "Research memo.pdf",
  tone: "file",
  body: [
    "Hi Matt. I reviewed the authorities in the government’s standing argument and checked each citation against the source opinion.",
    "Two authorities materially support the government’s proposition. Three are distinguishable on procedural posture, and two do not support the proposition as stated.",
    "I also found three stronger authorities for our position. Hover over any citation below to preview the holding or rule, then open it in the Vienna law library for the full text.",
  ],
  citations: [LAW.rule8, LAW.twombly, LAW.iqbal, LAW.rule15],
  actions: ["Draft the reply", "Review authorities"],
  confirm: "Reply draft saved to your Drafts",
};
const CitePreview = ({ citation, index, isDemoHovering = false }) => (
  <li className="hero-cite-item">
    <a href={citation.url} className={`hero-cite-link${isDemoHovering ? " is-demo-hovering" : ""}`} target="_blank" rel="noreferrer" aria-label={`Open ${citation.label} in the law library`}>
      <span className="hero-cite-number">{index + 1}</span>
      <span className="hero-cite-label">{citation.label}</span>
      <span className="hero-cite-popover" role="tooltip">
        <strong>{citation.label}</strong>
        <span>{citation.meta}</span>
        <span>{citation.preview}</span>
        <span className="hero-cite-popover__action">Open in law library →</span>
      </span>
    </a>
  </li>
);

/* Inline citation used inside a chat message. Same hover contract as the
   generated-document cites: preview on hover/focus, click opens the library. */
const Cite = ({ citation }) => (
  <a
    href={citation.url}
    className="hero-cite-inline"
    target="_blank"
    rel="noreferrer"
    aria-label={`Open ${citation.label} in the law library`}
  >
    {citation.label}
    <span className="hero-cite-popover" role="tooltip">
      <strong>{citation.label}</strong>
      <span>{citation.meta}</span>
      <span>{citation.preview}</span>
      <span className="hero-cite-popover__action">Open in law library &rarr;</span>
    </span>
  </a>
);

/* One film beat per email at most: a count tile with findings (film 3), a date rail (film 4) or a question
   that needs you (launch film). Entrances are CSS, staggered when the email or reply mounts. */
const Tally = ({ tally }) => (
  <div className="hero-tally" style={{ "--step": tally.dots.length > 12 ? "0.04s" : "0.12s" }}>
    <strong>{tally.count}</strong>
    <span>{tally.label}</span>
    <div className="hero-tally__dots" aria-hidden="true">
      {tally.dots.map((tone, i) => <i key={i} className={`is-${tone}`} style={{ "--i": i }} />)}
    </div>
    <small>{tally.legend}</small>
  </div>
);

const Findings = ({ items, receipt }) => (
  <>
    <ul className="hero-findings">
      {items.map((item, i) => (
        <li key={item.title} className={`is-${item.tone}`} style={{ "--i": i }}>
          <strong>{item.title}</strong>
          <span>{item.detail}</span>
        </li>
      ))}
    </ul>
    {receipt ? <p className="hero-receipt" style={{ "--i": items.length }}><TbCheck aria-hidden="true" /> {receipt}</p> : null}
  </>
);

const DateRail = ({ dates }) => (
  <ol className="hero-dates">
    {dates.map((date, i) => (
      <li key={date.label} className={`is-${date.type}`} style={{ "--i": i }}>
        <b>{date.day}</b>
        <span>{date.label}</span>
      </li>
    ))}
  </ol>
);

const Question = ({ question }) => (
  <div className="hero-question">
    <span className="hero-question__pill">Needs you</span>
    <p>{question}</p>
  </div>
);

/* The last beat of every story (films 3 and 4): the demo cursor presses the primary action, the button
   dips, and a confirmation lands. `pressed` is true from the click phase until the email or reply leaves. */
const Actions = ({ actions, confirm, pressed }) => (
  <div className="hero-chat-actions">
    {/* Once pressed, the secondary actions step aside so the confirmation sits beside the primary one. */}
    {(pressed ? actions.slice(0, 1) : actions).map((label, i) => (
      <span key={label} className={`hero-action${i === 0 ? " is-primary hero-click-target" : ""}${i === 0 && pressed ? " is-pressed" : ""}`}>{label}</span>
    ))}
    {confirm && pressed ? <span className="hero-action-confirm"><TbCheck aria-hidden="true" /> {confirm}</span> : null}
  </div>
);

/* Real agent behaviours (drawn from agent@ mail), not invented dashboard rows. */
const EMAIL_ROWS = [RESEARCH_EMAIL,
  {
    subject: "Draft finished: Bonilla Lagos v. Warden",
    preview: "Federal habeas draft completed and attached for review.",
    time: "8:32 AM",
    chip: "Draft: Bonilla Lagos Habeas Petition.docx",
    tone: "doc",
    body: [
      "Hi Matt. I’ve finished the federal habeas petition in Bonilla Lagos v. Warden and attached the draft for your review.",
      "I carried forward the grounds and relief from your notes. Mark any changes in the document and I’ll prepare a clean revision.",
    ],
    featureCard: "draftReview",
    documentTitle: "Bonilla Lagos v. Warden",
    fileName: "Bonilla Lagos Habeas Petition.docx",
    documentText: [
      "UNITED STATES DISTRICT COURT", "", "PETITION FOR WRIT OF HABEAS CORPUS", "", "Bonilla Lagos v. Warden", "",
      "PRELIMINARY STATEMENT", "", "Petitioner seeks relief under 28 U.S.C. § 2254 from his continued custody under the state-court judgment identified in the accompanying record. Respondent is the warden with custody of Petitioner. Rule 2(a), Rules Governing Section 2254 Cases. This draft organizes the grounds and requested relief from counsel’s notes for attorney review.", "",
      "GROUNDS FOR RELIEF", "", "Ground One — The record does not establish that the challenged ruling was harmless. On collateral review, relief turns on whether the error had a substantial and injurious effect or influence in determining the verdict. Brecht v. Abrahamson, 507 U.S. 619, 637 (1993). The supporting facts and record citations remain subject to counsel’s confirmation.", "",
      "Ground Two — The petition preserves the additional issue identified in counsel’s notes. Confirm the claim was fairly presented to the state courts before filing. 28 U.S.C. § 2254(b)(1)(A).", "",
      "PRAYER FOR RELIEF", "", "Petitioner respectfully requests that the Court grant the writ or such other relief as the record and law warrant.", "",
      "Respectfully submitted,", "Counsel for Petitioner",
    ].join("\n"),
    actions: ["Approve draft", "Request changes"],
    confirm: "Approved · clean copy saved to the matter",
  },
  {
    subject: "Missing docket item: KalshiEX LLC v. Knudsen",
    preview: "Order staying all case deadlines for 60 days is missing from the matter.",
    time: "7:41 AM",
    chip: "Docket update needed",
    tone: "info",
    body: [
      "While updating the KalshiEX LLC v. Knudsen docket I found an entry the matter file doesn’t have: the order staying all case deadlines for 60 days.",
      "I need it to recalculate the calendar.",
    ],
    question: "Can you send me the stay order? I’ll file it to the matter and move every affected deadline.",
    featureCard: null,
    actions: ["Upload document", "Forward the ECF notice"],
    confirm: "Stay order filed · deadlines recalculated",
  },
  {
    subject: "New filing: KalshiEX LLC v. Knudsen",
    preview: "Plaintiff objected to the magistrate judge's order. Docket and timeline updated.",
    time: "7:12 AM",
    chip: "Objection.pdf",
    tone: "file",
    body: [
      "Plaintiff filed an objection to the magistrate judge's order in KalshiEX LLC v. Knudsen this morning.",
      "I downloaded it, attached it to the matter, and updated the docket and matter timeline. The joint status update is still due August 21, so I opened the drafting task. Nothing for you to do unless you want to reassign it.",
    ],
    featureCard: "docket",
    actions: ["Open filing", "Reassign task"],
    confirm: "Opened in the KalshiEX matter",
  },
  {
    subject: "Deadlines to plan: Reyes and Apex Labs",
    preview: "Court and internal dates are mapped for both matters.",
    time: "8:18 AM",
    chip: "2 matters · dates mapped",
    tone: "alert",
    body: [
      "Two upcoming court dates need an owner. I set an internal draft date ahead of each.",
      "Assign or change any date and I’ll update the calendar and notify the team.",
    ],
    dates: [
      { day: "Sep 25", label: "Reyes · internal draft", type: "internal" },
      { day: "Sep 29", label: "Reyes · reply due", type: "court" },
      { day: "Sep 30", label: "Apex Labs · internal draft", type: "internal" },
      { day: "Oct 6", label: "Apex Labs · RFP responses due", type: "court" },
    ],
    featureCard: null,
    actions: ["Assign owners", "Open deadlines"],
    confirm: "Invites sent to the team",
  },
  {
    subject: "Discovery review ready: RFP Set Two",
    preview: "34 requests processed. Objection issues flagged and first-pass responses drafted.",
    time: "Yesterday",
    chip: "RFP Responses.docx",
    tone: "doc",
    body: [
      "Apex Labs, RFP Set Two: 34 requests received. The Word document is attached for your review.",
    ],
    tally: {
      count: "11 of 34",
      label: "requests already answered by matter files",
      dots: [...Array(11).fill("green"), ...Array(7).fill("amber"), ...Array(16).fill("grey")],
      legend: "11 matched to files · 7 likely objections · 16 need your input",
    },
    items: [
      { tone: "green", title: "11 requests matched to matter files.", detail: "Each linked to the documents that answer it." },
      { tone: "amber", title: "7 likely objection issues.", detail: "Flagged for your call. Nothing has been objected to yet." },
      { tone: "green", title: "First-pass responses drafted.", detail: "In prior firm language. Due October 6, now on the calendar." },
    ],
    receipt: "34 requests processed. Nothing has been served.",
    featureCard: null,
    actions: ["Review responses"],
    confirm: "RFP Responses.docx opened for your edits",
  },
  {
    subject: "Suggested meeting with Milan B.",
    preview: "I detected the follow-up in your email. Confirm and I’ll schedule it.",
    time: "Yesterday",
    chip: "Proposed: Wed, 11:00 AM ET",
    tone: "cal",
    body: [
      "Milan’s last email says he’d like to find time this week to walk through the pilot.",
      "Your calendar is open Wednesday at 11:00 AM ET. Reply confirm and I’ll book it, add a Zoom link, and send both invites.",
    ],
    actions: ["Confirm", "Suggest another time"],
    confirm: "Booked · Zoom added · invites sent",
  },
  {
    subject: "Cap table reconciliation ready",
    preview: "Founder grants, options, SAFEs, and warrants checked against the stock ledger.",
    time: "2 days ago",
    chip: "Fully diluted summary + open items.docx",
    tone: "file",
    body: [
      "I reconciled founder grants, options, SAFEs, and warrants against the latest stock ledger.",
      "The fully diluted summary is ready. Signed SAFEs and two option grant notices are still missing; review the open items before anything is finalized.",
    ],
    featureCard: "capTable",
    actions: ["Review open items", "Open reconciliation"],
    confirm: "Request for the signed SAFEs saved to your Drafts",
  },
];

/* `confirm` marks the message whose primary action the demo cursor presses at the end of the beat. */
const ChatPressContext = React.createContext(false);
const Msg = ({ from, time, children, actions, confirm, hideHeader = false, name, initials, className = "" }) => {
  const pressed = React.useContext(ChatPressContext);
  return (
    <div className={`hero-chat-msg${className ? ` ${className}` : ""}`}>
      {from === "agent" ? <AgentMark /> : <span className="hero-chat-avatar">{initials || "MK"}</span>}
      <div>
        {!hideHeader && <p className="hero-chat-msg__who">
          <strong>{from === "agent" ? "Vienna" : name || "Matt K."}</strong>
          {from === "agent" && <em>APP</em>}
          <small>{time}</small>
        </p>}
        {children}
        {actions?.length ? (
          confirm
            ? <Actions actions={actions} confirm={confirm} pressed={pressed} />
            : <div className="hero-chat-actions">{actions.map((label) => <span key={label} className="hero-action">{label}</span>)}</div>
        ) : null}
      </div>
    </div>
  );
};

/* A key date inside a reply rings once as the reply lands (film 1's "response due Oct 20"). */
const KeyDate = ({ children }) => <mark className="hero-key-date">{children}</mark>;

/* Reuse the actual startup feature-card animations in the hero. The card copy
   is intentionally hidden here; the visual pane is the same component used on
   the product page, so the landing response cannot drift into a second mock. */
const HERO_FEATURE_CARDS = {
  diligence: DiligenceRoomCard,
  docket: CourtSectionCard,
  matter: ProjectManagementCard,
  revenue: RevenueMetricsCard,
  capTable: CapTableCard,
};

const HeroFeatureCardVisual = ({ card, documentText, documentTitle, fileName }) => {
  if (card === "draftReview") {
    return (
      <div className="hero-feature-card-visual hero-feature-card-visual--draft-review">
        <EmailWorkflowAttachmentPreview text={documentText} documentTitle={documentTitle} fileName={fileName} openDelay={1200} />
      </div>
    );
  }

  if (card === "citationVerification") {
    return <div className="hero-feature-card-visual hero-feature-card-visual--citation"><InstantCiteVerification visualOnly /></div>;
  }

  const FeatureCard = HERO_FEATURE_CARDS[card];
  if (!FeatureCard) return null;
  // The docket email is about the Aug 18 objection, so the hero's docket opens just before it lands.
  const cardProps = card === "docket" ? { docketSeed: 7 } : {};
  return (
    <div className={`hero-feature-card-visual hero-feature-card-visual--${card}`}>
      <FeatureCard {...cardProps} />
    </div>
  );
};

/* One feed per channel: notice → why it matters → what was already done → smallest approval. */
const CHANNEL_FEEDS = {
  research: {
    purpose: "Ask for research in plain English; get verified, linked authorities back.",
    reading: ["Reading their brief", "Checking 7 authorities", "Writing the reply"],
    mobileMessages: (
      <>
        <Msg from="mark" time="10:14 AM">
          <p><span className="hero-chat-mention">@Vienna</span> check whether opposing counsel&rsquo;s cases actually support their standing argument, and link the strongest authorities.</p>
        </Msg>
        <Msg from="agent" time="10:21 AM" actions={["Draft the reply"]} confirm="Reply draft saved to your Drafts">
          <p><strong>2 of 7 materially support the point.</strong> 3 are distinguishable on posture; 2 do not support the proposition as cited.</p>
          <p>The brief leans on <Cite citation={LAW.twombly} /> but leaves out <Cite citation={LAW.iqbal} />, the other key pleading-standard case.</p>
          <div className="hero-cite-panel hero-cite-panel--chat">
            <div className="hero-cite-panel__heading"><strong>Authorities checked</strong><span>Tap to open</span></div>
            <ol className="hero-cite-list">
              {[LAW.twombly, LAW.iqbal, LAW.rule15].map((citation, index) => <CitePreview key={citation.label} citation={citation} index={index} />)}
            </ol>
          </div>
        </Msg>
      </>
    ),
    messages: (
      <>
        <Msg from="mark" time="10:14 AM">
          <p><span className="hero-chat-mention">@Vienna</span> check whether opposing counsel&rsquo;s cases actually support their standing argument</p>
        </Msg>
        <Msg from="agent" time="10:21 AM" actions={["Draft the reply", "Review authorities"]} confirm="Reply draft saved to your Drafts">
          <p>I pulled all 7 authorities from their brief and checked each one against the source opinion.</p>
          <p><strong>2 of 7 materially support their proposition.</strong> 3 are distinguishable on procedural posture &mdash; each was decided at summary judgment, not on a <Cite citation={LAW.rule8} /> pleading challenge, so the standard they invoke was never applied. 2 do not support the proposition as stated.</p>
          <p>Their brief relies on <Cite citation={LAW.twombly} /> but omits <Cite citation={LAW.iqbal} />. The strongest response is the difference in procedural posture; I flagged the <Cite citation={LAW.rule15} /> amendment option for counsel to confirm against the case history and timing.</p>
          <p>Every cite below is verified against the source text in the Vienna law library. Hover to preview, click to open.</p>
          <div className="hero-cite-panel hero-cite-panel--chat">
            <div className="hero-cite-panel__heading"><strong>Authorities checked</strong><span>Hover to preview &middot; click to open</span></div>
            <ol className="hero-cite-list">{[LAW.rule8, LAW.twombly, LAW.iqbal, LAW.rule15].map((citation, index) => <CitePreview key={citation.label} citation={citation} index={index} />)}</ol>
          </div>
        </Msg>
      </>
    ),
  },
  matters: {
    purpose: "Track what changed across all active matters.",
    reading: ["Reading overnight filings", "Checking 3 matters", "Writing the brief"],
    messages: (
      <>
        <Msg from="mark" time="8:01 AM">
          <p><span className="hero-chat-mention">@Vienna</span> what changed across my matters since yesterday? Give me the short version and anything that needs approval.</p>
        </Msg>
        <Msg from="agent" time="8:06 AM" actions={["Send reminder", "Review morning brief"]} confirm="Reminder sent for the founder signature">
          <p><strong>3 matters need attention.</strong></p>
          <ul>
            <li><strong>Reyes v. United States:</strong> Government filed its opposition. I downloaded the filing, checked all 14 citations, and added the <KeyDate>September 29</KeyDate> reply date. Internal draft date: September 25.</li>
            <li><strong>Apex Labs:</strong> RFP Set Two is organized against 11 matter files; 7 objection issues are flagged and first-pass responses are ready to review for October 6.</li>
            <li><strong>Apex Labs engagement letter:</strong> One founder signature is still pending. A reminder is ready for approval.</li>
          </ul>
          <p>Everything else is on track.</p>
          <div className="hero-chat-brief">
            <strong>Morning brief</strong>
            <span>2 new filings · 4 deadlines · 1 signature pending · 2h 47m tracked · $677.50 today · 12.4h this week</span>
          </div>
          <HeroFeatureCardVisual card="matter" />
        </Msg>
      </>
    ),
  },
  deadlines: {
    purpose: "Every date Vienna is watching, and the ones it isn’t sure about.",
    reading: ["Reading today’s filings", "Computing the dates", "Checking owners"],
    messages: (
      <>
        <Msg from="mark" time="8:04 AM">
          <p><span className="hero-chat-mention">@Vienna</span> flag the new dates from today&rsquo;s filings, separate court and internal dates, and show me any task without an owner.</p>
        </Msg>
        <Msg from="agent" time="8:06 AM" actions={["Open deadline"]}>
          <p><strong>New deadline: Reyes v. United States</strong></p>
          <p>Reply to the government’s opposition is due <KeyDate>September 29</KeyDate>. I calculated it from the filing and the applicable rules, added it to the matter calendar, and set an internal draft date of September 25.</p>
          <div className="hero-tracking">
            <strong>Dates I’m tracking</strong>
            <ul>
              <li style={{ "--i": 0 }}><b>Sep 29</b><span>Reyes v. United States<small>Reply to opposition</small></span><em className="is-court">Court</em></li>
              <li style={{ "--i": 1 }}><b>Sep 25</b><span>Reyes v. United States<small>Internal draft</small></span><em className="is-internal">Internal</em></li>
              <li style={{ "--i": 2 }}><b>&mdash;</b><span>United States v. Stewart<small>Arraignment prep</small></span><em className="is-owner">No owner</em></li>
            </ul>
          </div>
        </Msg>
        <Msg from="agent" time="9:12 AM" actions={["Assign to me", "Open task"]} confirm="Assigned to you · task list updated">
          <p><strong>Unassigned: arraignment prep · United States v. Stewart</strong></p>
          <p>The charging document is in the matter, but the prep task has no owner. Assign it here and I’ll update the task list; time can be recorded when the work is complete.</p>
        </Msg>
      </>
    ),
  },
  filings: {
    purpose: "New docket activity, already pulled into the matter.",
    reading: ["Reading the new filing", "Checking 14 citations", "Updating the matter"],
    messages: (
      <>
        <Msg from="mark" time="7:56 AM">
          <p><span className="hero-chat-mention">@Vienna</span> pull new filings into the right matters, check the citations, and tell me what changed and what is due next.</p>
        </Msg>
        <Msg from="agent" time="7:58 AM" actions={["Open filing"]} confirm="Opened in the Reyes matter">
          <p><strong>New filing: Reyes v. United States</strong></p>
          <p>Government Opposition to Motion to Suppress filed overnight. I:</p>
          <ul>
            <li>downloaded the filing and attached it to the matter</li>
            <li>extracted the new arguments</li>
            <li>checked 14 cited authorities</li>
            <li>added the September 29 reply deadline</li>
          </ul>
          <p><strong>13 citations verified; 1 potential mismatch flagged</strong> for attorney review.</p>
          <HeroFeatureCardVisual card="citationVerification" />
        </Msg>
      </>
    ),
  },
  discovery: {
    purpose: "Requests in, first-pass responses out.",
    reading: ["Reading RFP Set Two", "Matching 34 requests", "Drafting responses"],
    messages: (
      <>
        <Msg from="mark" time="Yesterday 4:32 PM">
          <p><span className="hero-chat-mention">@Vienna</span> post the discovery-review status for RFP Set Two, including the flagged objections and the response deadline.</p>
        </Msg>
        <Msg from="agent" time="Yesterday 4:40 PM" actions={["Review responses"]} confirm="Opened for your edits">
          <p><strong>Apex Labs: RFP Set Two processed</strong></p>
          <p>34 requests received. I:</p>
          <ul>
            <li>matched 11 requests to existing files</li>
            <li>flagged 7 likely objection issues</li>
            <li>added the October 6 response deadline</li>
            <li>drafted first-pass responses using prior firm language</li>
          </ul>
        </Msg>
      </>
    ),
  },
  clients: {
    purpose: "Client email, follow-ups, and meetings handled with one word.",
    reading: ["Reading Milan’s email", "Checking your calendar", "Drafting the confirmation"],
    mobileMessages: (
      <>
        <Msg from="teammate" name="Dana R." initials="DR" time="Yesterday 11:40 AM">
          <p><span className="hero-chat-mention">@Vienna</span> what&rsquo;s the latest on the Apex Labs financing?</p>
        </Msg>
        <Msg from="agent" time="Yesterday 11:40 AM" className="is-wall">
          <p className="hero-wall"><TbLock aria-hidden="true" /> <span>Dana, you&rsquo;re not on the Apex Labs matter, so I can&rsquo;t share anything from it. Matt K. can add you.</span></p>
        </Msg>
        <Msg from="mark" time="2:04 PM">
          <p><span className="hero-chat-mention">@Vienna</span> find time with Milan to walk through the pilot; draft a confirmation and wait before sending the invite.</p>
        </Msg>
        <Msg from="agent" time="2:05 PM">
          <p><strong>Suggested meeting with Milan B.</strong></p>
          <p>He wants to walk through the pilot. You&rsquo;re open <strong>Wed 11:00 AM ET</strong>. Reply <strong>confirm</strong>.</p>
        </Msg>
        <Msg from="mark" time="2:11 PM"><p>confirm</p></Msg>
        <Msg from="agent" time="2:11 PM" actions={["Open invite"]} confirm="Opened in your calendar">
          <p>Booked. Zoom added, both invites sent, prep posts here 15 minutes before.</p>
        </Msg>
      </>
    ),
    messages: (
      <>
        <Msg from="teammate" name="Dana R." initials="DR" time="Yesterday 11:40 AM">
          <p><span className="hero-chat-mention">@Vienna</span> what&rsquo;s the latest on the Apex Labs financing?</p>
        </Msg>
        <Msg from="agent" time="Yesterday 11:40 AM" className="is-wall">
          <p className="hero-wall"><TbLock aria-hidden="true" /> <span>Dana, you&rsquo;re not on the Apex Labs matter, so I can&rsquo;t share anything from it. Matt K. can add you.</span></p>
        </Msg>
        <Msg from="mark" time="Yesterday 2:04 PM">
          <p><span className="hero-chat-mention">@Vienna</span> find time with Milan to walk through the pilot; draft a confirmation and wait before sending the invite.</p>
        </Msg>
        <Msg from="agent" time="Yesterday 2:05 PM">
          <p><strong>Suggested meeting with Milan B.</strong></p>
          <p>Milan’s email says he wants to find time this week to walk through the pilot. Your calendar is open <strong>Wednesday · 11:00 AM ET</strong>.</p>
          <p>Reply <strong>confirm</strong> and I’ll book it, add Zoom, and send both calendar invites.</p>
        </Msg>
        <Msg from="mark" time="Yesterday 2:11 PM">
          <p>confirm</p>
        </Msg>
        <Msg from="agent" time="Yesterday 2:11 PM" actions={["Open invite"]} confirm="Opened in your calendar">
          <p>Booked for Wednesday at 11:00 AM ET. Zoom link added, invites sent to you and Milan, and meeting prep will post here 15 minutes before.</p>
        </Msg>
      </>
    ),
  },
  billing: {
    purpose: "A diligence room that stays ready for investor review.",
    reading: ["Reading the data room", "Checking the stock ledger", "Listing what’s missing"],
    mobileMessages: (
      <>
        <Msg from="mark" time="7:28 AM"><p><span className="hero-chat-mention">@Vienna</span> check whether the diligence room is ready for this week&rsquo;s investor meetings and list anything still missing.</p></Msg>
        <Msg from="agent" time="7:30 AM" actions={["Open checklist"]} confirm="Request for the signed SAFEs saved to your Drafts">
          <p><strong>Diligence room is 64% ready</strong></p>
          <p>I verified the corporate records, stock ledger, and metrics. Signed SAFEs and two option grant notices still need attention.</p>
          <HeroFeatureCardVisual card="diligence" />
          <HeroFeatureCardVisual card="revenue" />
        </Msg>
      </>
    ),
    messages: (
      <>
        <Msg from="mark" time="7:28 AM"><p><span className="hero-chat-mention">@Vienna</span> check whether the diligence room is ready for this week&rsquo;s investor meetings and list anything still missing.</p></Msg>
        <Msg from="agent" time="7:30 AM" actions={["Open checklist"]} confirm="Request for the signed SAFEs saved to your Drafts">
          <p><strong>Diligence room is 64% ready</strong></p>
          <p>Corporate records, the stock ledger, and the MRR / ARR source are verified. Signed SAFEs and two option grant notices still need attention before the room is shared.</p>
          <HeroFeatureCardVisual card="diligence" />
          <HeroFeatureCardVisual card="revenue" />
        </Msg>
      </>
    ),
  },
};

const CHANNELS = Object.keys(CHANNEL_FEEDS);

/* The compact layout uses the same complete loop as desktop. The active
   surface shows one state at a time, so every row/channel can still cycle
   without making the mobile card permanently dense. */
const MOBILE_EMAIL_SUBJECTS = [
  "Caselaw research: opposition authorities checked",
  "Draft finished: Bonilla Lagos v. Warden",
  "Cap table reconciliation ready",
];
const MOBILE_CHANNELS = ["filings", "matters", "billing"];
/* Narrow layouts show 3 of the 8 requests; these map storyboard item -> request. */
const MOBILE_EMAIL_INDEXES = [0, 1, 7];
const MOBILE_CHANNEL_INDEXES = [3, 1, 6];
/* Eight email beats map onto the seven existing collaboration examples by topic. */
const DESKTOP_CHANNEL_INDEXES = [0, 1, 2, 3, 2, 4, 5, 6];
const NARROW_QUERY = "(max-width: 680px)";

/* Typing speed drives the compose phase length so no request is ever cut off:
   composer pop-in, one character per tick, then a short hold on the full text. */
const TYPE_TICK_MS = 28;
const composeDuration = (text = "") => 400 + text.length * TYPE_TICK_MS + 700;

/* Every pointer beat measures its real target instead of guessing a percentage.
   Click phases are 900ms: 450ms travel, then the press + ripple land. */
const CLICK_PHASE_MS = 900;
const MANUAL_SEND_MS = 1100;
const MANUAL_DELIVERY_MS = 1400;
const CURSOR_HOTSPOT = { x: 6.67, y: 3.63 };
const CURSOR_TARGETS = {
  "email-plus-click": ".hero-mail-plus",
  "email-compose": ".hero-demo-caret",
  "email-send-click": ".hero-mail-compose__send-icon",
  "email-row-click": ".hero-mail-row.is-demo-target",
  "email-caselaw-hover": ".hero-cite-link.is-demo-hovering",
  "email-back-click": ".hero-mail-back",
  "collab-click": ".hero-surface-tab:last-child",
  "slack-channel-click": ".hero-chat-channel.is-demo-target",
  "slack-compose": ".hero-demo-caret",
  "slack-send-click": ".hero-chat-compose__status svg",
  "email-surface-click": ".hero-surface-tab:first-child",
  "email-action-click": ".hero-mail-open .hero-click-target",
  "slack-action-click": ".hero-chat-feed .hero-click-target",
};
const CLICK_PHASES = new Set([
  "email-plus-click", "email-send-click", "email-row-click", "email-back-click",
  "collab-click", "slack-channel-click", "slack-send-click", "email-surface-click",
  "email-action-click", "slack-action-click",
]);
const COMPOSE_STAGE_PHASES = new Set(["email-compose", "email-send-click", "email-sending"]);

/* How long an opened email stays open: long enough for the card inside it to finish its own beat
   (the draft is read to the bottom of the page; the docket files one entry and summarizes it). */
const RESPONSE_HOLD_MS = { draftReview: 8200, docket: 8800, capTable: 5200, findings: 6200, default: 3500 };
/* The closing beat of every story: the cursor presses the primary action and its confirmation holds. */
const ACTION_PHASE_MS = 1800;
const responseHold = (row) => RESPONSE_HOLD_MS[row?.featureCard] || (row?.items ? RESPONSE_HOLD_MS.findings : RESPONSE_HOLD_MS.default);
/* Every authority in the research email gets its own hover beat. */
const CITE_HOVER_MS = 1400;

/* What Matt emails Vienna for each story. Each request is what a lawyer would actually send, and Vienna's
   reply (EMAIL_ROWS, same order) answers it; the reply only lands in the inbox after this is sent. */
const DEMO_SUBJECTS = [
  "Reyes v. United States: their standing cases",
  "Bonilla Lagos habeas petition",
  "KalshiEX: update the docket",
  "KalshiEX: anything new filed?",
  "Reyes and Apex Labs: what’s coming up",
  "Apex Labs: RFP Set Two",
  "Milan’s pilot walkthrough",
  "Cap table before the raise",
];

const DEMO_REQUESTS = {
  email: [
    "Can you check every case in the government’s standing argument in Reyes against the source opinions? Tell me which ones actually hold up, and link anything stronger for us.",
    "My notes for the Bonilla Lagos habeas petition are in the matter. Can you turn them into a draft I can review?",
    "Can you bring the KalshiEX docket up to date and recalculate our deadlines from it?",
    "Did anything get filed in KalshiEX overnight? Pull it into the matter and tell me if it changes what’s due.",
    "What’s coming up on Reyes and Apex Labs? Separate the court deadlines from our internal draft dates and tell me what needs an owner.",
    "Apex Labs’ RFP Set Two just came in. Can you match the requests to our files, flag likely objections, and draft first-pass responses?",
    "Milan wants to walk through the pilot this week. Can you find a time that works for me and get it ready to confirm?",
    "Before the raise, can you reconcile founder grants, options, SAFEs, and warrants against the stock ledger and tell me what’s missing?",
  ],
  slack: [
    "check whether opposing counsel's cases actually support their standing argument, and link the strongest authorities.",
    "what changed across my matters since yesterday? Give me the short version and anything that needs approval.",
    "flag the new dates from today's filings, separate court and internal dates, and show me any task without an owner.",
    "pull new filings into the right matters, check the citations, and tell me what changed and what is due next.",
    "post the discovery-review status for RFP Set Two, including the flagged objections and the response deadline.",
    "find time with Milan to walk through the pilot; draft a confirmation and wait before sending the invite.",
    "check whether the diligence room is ready for this week’s investor meetings and list anything still missing.",
  ],
};

/* Desktop keeps every row and every word; only the narrow layout trims. */
const useIsNarrow = () => {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia(NARROW_QUERY);
    const update = () => setIsNarrow(query.matches);
    update();

    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", update);
      return () => query.removeEventListener("change", update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  return isNarrow;
};


const HeroSection = ({ onWatchDemo, onSeasonalAnimationStart }) => {

  // Type both lines, then hand the channel line to the rotator without changing its appearance.
  const [typedCount, setTypedCount] = useState(0);
  const homeTypingComplete = typedCount >= HOME_TITLE_TEXT.length;
  const [isChannelFinalPhraseHolding, setIsChannelFinalPhraseHolding] = useState(false);

  const [surface, setSurface] = useState("email"); // email | collab
  const [openEmail, setOpenEmail] = useState(null);
  const [channel, setChannel] = useState("research");
  const [demoPhase, setDemoPhase] = useState("email-inbox");
  const [demoPaused, setDemoPaused] = useState(false);
  const [demoRequest, setDemoRequest] = useState("");
  const [demoExample, setDemoExample] = useState(0);
  // Replies delivered in this pass of the demo, newest first. They sit on top of the older mail.
  const [delivered, setDelivered] = useState([]);
  // Emails that have been opened: their title drops from bold and the unread dot goes, like a real inbox.
  const [readRows, setReadRows] = useState([]);
  const [demoAuthorityIndex, setDemoAuthorityIndex] = useState(0);
  const [demoCursorPosition, setDemoCursorPosition] = useState(null);
  const [planeOrigin, setPlaneOrigin] = useState(null);
  const storyboardIndexRef = useRef(0);
  const storyboardTimerRef = useRef(null);
  const manualPauseTimerRef = useRef(null);
  const manualTransitionTimerRef = useRef(null);
  const isNarrow = useIsNarrow();

  const handleChannelFinalPhraseHoldChange = useCallback((isHolding) => {
    setIsChannelFinalPhraseHolding(isHolding);
  }, []);

  // Seasonal weather falls each time the value word rolls from "ROI" back to
  // "overload" (once per headline cycle).
  const handleHeroValueLoopComplete = useCallback(() => {
    onSeasonalAnimationStart?.();
  }, [onSeasonalAnimationStart]);

  // The landing-page demo is a storyboard, not a real composer. It reuses the
  // same inbox/channel surfaces below and only changes their presentation state.
  useEffect(() => {
    if (demoPaused) return undefined;

    const storyboard = [];
    const emailCount = isNarrow ? MOBILE_EMAIL_SUBJECTS.length : EMAIL_ROWS.length;
    for (let itemIndex = 0; itemIndex < emailCount; itemIndex += 1) {
      const emailIndex = isNarrow ? MOBILE_EMAIL_INDEXES[itemIndex] : itemIndex;
      const channelIndex = isNarrow ? MOBILE_CHANNEL_INDEXES[itemIndex] : DESKTOP_CHANNEL_INDEXES[itemIndex];
      const channelStepIndex = isNarrow ? itemIndex : channelIndex;
      // Send beat: click Send -> plane leaves the composer (sending) -> the
      // reply lands in the inbox (delivered) -> the pointer opens it.
      storyboard.push(
        ["email-inbox", 2200, itemIndex],
        ["email-plus-click", CLICK_PHASE_MS, itemIndex],
        ["email-compose", composeDuration(DEMO_REQUESTS.email[emailIndex]), itemIndex],
        ["email-send-click", CLICK_PHASE_MS, itemIndex],
        ["email-sending", 1100, itemIndex],
        ["email-delivered", 1400, itemIndex],
        ["email-row-click", CLICK_PHASE_MS, itemIndex],
        ["email-response", responseHold(EMAIL_ROWS[emailIndex]), itemIndex],
      );
      if (itemIndex === 0) {
        // Narrow layouts list the first 3 authorities only.
        const citeCount = Math.min((EMAIL_ROWS[0].citations || []).length, isNarrow ? 3 : Infinity);
        storyboard.push(["email-caselaw-open", 1400, itemIndex], ["email-caselaw-hover", citeCount * CITE_HOVER_MS + 300, itemIndex]);
      }
      storyboard.push(
        ["email-action-click", ACTION_PHASE_MS, itemIndex],
        ["email-back-click", CLICK_PHASE_MS, itemIndex],
        ["email-inbox", 700, itemIndex],
        ["collab-click", CLICK_PHASE_MS, channelStepIndex],
        ["slack-channel-click", CLICK_PHASE_MS, channelStepIndex],
        ["slack-channel", 3000, channelStepIndex],
        ["slack-compose", composeDuration(DEMO_REQUESTS.slack[channelIndex]), channelStepIndex],
        ["slack-send-click", CLICK_PHASE_MS, channelStepIndex],
        ["slack-agent-typing", 1800, channelStepIndex],
        ["slack-response", channelIndex === 3 ? 6000 : 3200, channelStepIndex],
        ["slack-action-click", ACTION_PHASE_MS, channelStepIndex],
        ["email-surface-click", CLICK_PHASE_MS, itemIndex],
      );
    }

    if (!storyboard.length) {
      storyboardIndexRef.current = 0;
      return undefined;
    }
    storyboardIndexRef.current = normalizeStoryboardIndex(storyboardIndexRef.current, storyboard.length);

    const advance = () => {
      storyboardIndexRef.current = normalizeStoryboardIndex(storyboardIndexRef.current, storyboard.length);
      const currentStep = storyboard[storyboardIndexRef.current];
      if (!Array.isArray(currentStep)) return;
      const [phase, duration, itemIndex] = currentStep;
      setDemoPhase(phase);
      if (phase === "email-row-click") {
        setReadRows((list) => (list.includes(itemIndex) ? list : [...list, itemIndex]));
      }
      if (phase.startsWith("slack-")) setSurface("collab");
      // email-surface-click happens ON the collab surface: the tab only changes
      // once that click has landed, i.e. at the next email-inbox phase.
      if (phase.startsWith("email-") && phase !== "email-surface-click") setSurface("email");
      if (phase === "email-inbox" || phase === "email-delivered") setOpenEmail(null);
      if (phase === "email-compose") setDemoExample(itemIndex);
      // A new pass of the demo starts from the older mail only.
      if (phase === "email-plus-click" && itemIndex === 0) { setDelivered([]); setReadRows([]); }
      if (phase === "email-response" || phase === "email-caselaw-open" || phase === "email-caselaw-hover") setOpenEmail(itemIndex);
      if (phase === "slack-channel") setChannel((isNarrow ? MOBILE_CHANNELS : CHANNELS)[itemIndex]);
      if (phase === "slack-channel") document.querySelector(".hero-chat-feed")?.scrollTo?.({ top: 0 });
      storyboardIndexRef.current = normalizeStoryboardIndex(storyboardIndexRef.current + 1, storyboard.length);
      storyboardTimerRef.current = setTimeout(advance, duration);
    };
    advance();
    return () => clearTimeout(storyboardTimerRef.current);
  }, [demoPaused, isNarrow]);

  useEffect(() => {
    if (demoPhase !== "email-caselaw-hover") return undefined;
    const citations = EMAIL_ROWS[0].citations || [];
    setDemoAuthorityIndex(0);
    const timer = setInterval(() => {
      setDemoAuthorityIndex((index) => (index + 1) % citations.length);
    }, CITE_HOVER_MS);
    return () => clearInterval(timer);
  }, [demoPhase]);

  // The pointer only ever moves to a measured element. Phases without a target
  // leave it parked where the last beat ended, so it never drifts or snaps back.
  useEffect(() => {
    const selector = CURSOR_TARGETS[demoPhase];
    if (!selector) return undefined;

    let frame = 0;
    const positionCursor = () => {
      const card = document.querySelector(".hero-inbox-card");
      const matches = document.querySelectorAll(selector);
      const target = matches[matches.length - 1];
      if (card && target) {
        const cardRect = card.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        // If the target sits below (or above) the visible part of the email body or chat feed, glide that
        // scroll area to it, like the films' camera following the action, and aim at where it will land.
        let shift = 0;
        const scroller = target.closest(".hero-mail-open__body, .hero-chat-feed");
        if (scroller) {
          const box = scroller.getBoundingClientRect();
          // Closing clicks leave room below for the confirmation that lands next to (or under) the button.
          const margin = demoPhase.endsWith("action-click") ? 44 : 12;
          if (targetRect.bottom > box.bottom - margin) shift = targetRect.bottom - (box.bottom - margin);
          else if (targetRect.top < box.top + 8) shift = targetRect.top - (box.top + 8);
          shift = Math.max(-scroller.scrollTop, Math.min(shift, scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop));
          if (shift) scroller.scrollBy?.({ top: shift, behavior: "smooth" });
        }
        // The SVG cursor hotspot is about 7px from its left edge and 4px
        // from its top edge. Aim at the center of the actual control.
        setDemoCursorPosition({
          left: targetRect.left - cardRect.left + (targetRect.width / 2) - CURSOR_HOTSPOT.x - (cardRect.width * 0.48),
          top: targetRect.top - shift - cardRect.top + (targetRect.height / 2) - CURSOR_HOTSPOT.y - 72,
        });
      }
    };
    frame = window.requestAnimationFrame(positionCursor);
    return () => window.cancelAnimationFrame(frame);
    // demoRequest keeps the parked pointer on the caret while the request types.
  }, [demoPhase, demoAuthorityIndex, demoExample, isNarrow, demoRequest]);

  useEffect(() => {
    if (demoPhase !== "email-delivered") return;
    setDelivered((list) => [demoExample, ...list.filter((index) => index !== demoExample)]);
    setReadRows((list) => list.filter((index) => index !== demoExample));
  }, [demoPhase, demoExample]);

  // The paper plane launches from the Send icon itself, measured against the card.
  useEffect(() => {
    if (demoPhase !== "email-sending") {
      setPlaneOrigin(null);
      return undefined;
    }
    const card = document.querySelector(".hero-inbox-card");
    const icon = document.querySelector(".hero-mail-compose__send-icon");
    if (card && icon) {
      const cardRect = card.getBoundingClientRect();
      const iconRect = icon.getBoundingClientRect();
      setPlaneOrigin({
        left: iconRect.left - cardRect.left + (iconRect.width / 2) - 14,
        top: iconRect.top - cardRect.top + (iconRect.height / 2) - 14,
      });
    }
    return undefined;
  }, [demoPhase]);

  useEffect(() => {
    const emailIndex = isNarrow ? MOBILE_EMAIL_INDEXES[demoExample] : demoExample;
    const channelIndex = isNarrow ? MOBILE_CHANNEL_INDEXES[demoExample] : DESKTOP_CHANNEL_INDEXES[demoExample];
    const request = demoPhase.includes("email")
      ? DEMO_REQUESTS.email[emailIndex]
      : DEMO_REQUESTS.slack[channelIndex];
    const isRequestPhase = demoPhase.includes("compose") || demoPhase === "email-send-click" || demoPhase === "slack-send-click";
    if (!isRequestPhase) {
      if (demoPhase !== "email-sending" && demoPhase !== "slack-agent-typing" && demoPhase !== "slack-response") setDemoRequest("");
      return undefined;
    }
    if (demoPhase === "email-send-click" || demoPhase === "slack-send-click") return undefined;
    let position = 0;
    setDemoRequest("");
    const timer = setInterval(() => {
      position += 1;
      setDemoRequest(request.slice(0, position));
      if (position >= request.length) clearInterval(timer);
    }, TYPE_TICK_MS);
    return () => clearInterval(timer);
  }, [demoPhase, demoExample, isNarrow]);

  useEffect(() => {
    if (homeTypingComplete) return undefined;
    const timeout = setTimeout(() => setTypedCount((count) => count + 1), 40);
    return () => clearTimeout(timeout);
  }, [homeTypingComplete, typedCount]);


  const renderHomeTitle = () => {
    const mainCount = Math.min(typedCount, HOME_TITLE.length);
    const channelStart = HOME_TITLE.length + 1;
    const channelCount = Math.min(Math.max(typedCount - channelStart, 0), HOME_CHANNEL_INITIAL.length);
    const typingCaret = <span className="cursor cursor--typing" aria-hidden="true">|</span>;
    return (
      <>
        {HOME_TITLE.slice(0, mainCount)}
        {!homeTypingComplete && typedCount <= HOME_TITLE.length ? typingCaret : null}
        <span className="hero-title__untyped">{HOME_TITLE.slice(mainCount)}</span>
        <span className="hero-title__channel-line">
          {homeTypingComplete ? (
            <ChannelRotator
              prefix={CHANNEL_PREFIX}
              suffix={CHANNEL_SUFFIX}
              words={CHANNEL_WORDS}
              lastWordDelay={13000}
              finalPhrase={CHANNEL_FINAL}
              start
              onFinalPhraseHoldChange={handleChannelFinalPhraseHoldChange}
            />
          ) : (
            <>
              {HOME_CHANNEL_INITIAL.slice(0, channelCount)}
              {typedCount >= channelStart ? typingCaret : null}
              <span className="hero-title__untyped">{HOME_CHANNEL_INITIAL.slice(channelCount)}</span>
            </>
          )}
          {homeTypingComplete ? cursor : null}
        </span>
      </>
    );
  };

  const cursor = <span className="cursor" aria-hidden="true">|</span>;
  const emailRows = isNarrow
    ? EMAIL_ROWS.filter((row) => MOBILE_EMAIL_SUBJECTS.includes(row.subject))
    : EMAIL_ROWS;
  const channels = isNarrow ? MOBILE_CHANNELS : CHANNELS;
  // Narrowing can strip the open row or the selected channel out from under us.
  const activeChannel = channels.includes(channel) ? channel : channels[0];
  const demoChannelTarget = isNarrow
    ? MOBILE_CHANNELS[demoExample % MOBILE_CHANNELS.length]
    : CHANNELS[DESKTOP_CHANNEL_INDEXES[demoExample % DESKTOP_CHANNEL_INDEXES.length]];
  const current = openEmail === null ? null : emailRows[openEmail] || null;
  // [row, index] in display order: replies delivered this pass (newest first), then the rest of the
  // demo emails. While the demo runs, the reply still being requested stays out until it lands on top;
  // once the visitor takes over, every email is in the inbox and openable. Shared by the inbox and the
  // compose backdrop so the rows never reshuffle when the composer opens.
  const pendingIndex = !demoPaused && !delivered.includes(demoExample) ? demoExample : null;
  const orderedRows = [
    ...delivered.filter((index) => emailRows[index]).map((index) => [emailRows[index], index]),
    ...emailRows.map((row, index) => [row, index]).filter(([, index]) => !delivered.includes(index) && index !== pendingIndex),
  ];
  // Delivered replies read as recent: the newest "Just now", earlier ones a few minutes back.
  const rowTime = (row, index) => {
    const age = delivered.indexOf(index);
    if (age < 0) return row.time;
    return age === 0 ? "Just now" : `${age * 3} min ago`;
  };
  const feed = CHANNEL_FEEDS[activeChannel];
  const isSlackCompose = demoPhase === "slack-compose";
  const isSlackSend = demoPhase === "slack-send-click";
  const isSlackResponse = demoPhase === "slack-response" || demoPhase === "slack-action-click";
  // The pressed state holds from the click until the email or reply leaves the screen.
  const emailPressed = demoPhase === "email-action-click" || demoPhase === "email-back-click";
  const slackPressed = demoPhase === "slack-action-click" || demoPhase === "email-surface-click";
  // Visitor clicks and the scripted pointer opening an email both mark it read.
  const markRead = (index) => setReadRows((list) => (list.includes(index) ? list : [...list, index]));

  const pauseDemo = () => {
    setDemoPaused(true);
    clearTimeout(manualPauseTimerRef.current);
    manualPauseTimerRef.current = setTimeout(() => setDemoPaused(false), 30000);
  };

  const takeOver = () => {
    pauseDemo();
    clearTimeout(manualTransitionTimerRef.current);
    setDemoPhase("manual");
  };

  // Any press on the card hands it to the visitor. Presses inside a composer keep it open so its own
  // Send button still works; everywhere else the scripted step (composer, typing, hover) is dropped.
  const handleCardPointerDown = (event) => {
    if (event.target.closest?.(".hero-mail-compose-stage, .hero-chat-compose")) {
      pauseDemo();
      return;
    }
    takeOver();
  };

  const sendManualSlackMessage = () => {
    pauseDemo();
    setSurface("collab");
    setDemoPhase("slack-agent-typing");
  };

  const closeEmailPopout = () => {
    pauseDemo();
    clearTimeout(manualTransitionTimerRef.current);
    setOpenEmail(null);
    setDemoPhase("email-inbox");
  };

  const sendManualEmail = () => {
    pauseDemo();
    clearTimeout(manualTransitionTimerRef.current);
    setOpenEmail(null);
    setDemoPhase("email-sending");
    manualTransitionTimerRef.current = setTimeout(() => {
      setDemoPhase("email-delivered");
      manualTransitionTimerRef.current = setTimeout(() => {
        setOpenEmail(demoExample);
        setDemoPhase("email-response");
      }, MANUAL_DELIVERY_MS + CLICK_PHASE_MS);
    }, MANUAL_SEND_MS);
  };

  useEffect(() => () => {
    clearTimeout(manualPauseTimerRef.current);
    clearTimeout(manualTransitionTimerRef.current);
  }, []);

  return (
    <section className="hero-section hero-section--split" aria-labelledby="hero-title">
      <div className="hero-split__left">
        {/* The value word (overload / adoption / ROI) rolls here once per headline cycle. */}
        <HeroBadge className="hero-value-badge">
          <span className="hero-value-badge__phrase">
            Legal tech{" "}
            <ChannelRotator
              words={HERO_VALUE_WORDS}
              start={isChannelFinalPhraseHolding}
              loopWords
              loopOnce
              startDelay={1400}
              interval={2200}
              onLoopComplete={handleHeroValueLoopComplete}
              finalPhrase=""
              emphasizeWord={false}
              className="hero-value-rotator"
            />{" "}
            solved.
          </span>
        </HeroBadge>

        <h1
          id="hero-title"
          className="hero-title typing-title hero-title--typing"
          aria-label={`${HOME_TITLE} over ${CHANNEL_FINAL}`}
        >
          {renderHomeTitle()}
        </h1>

        <div className="hero-actions">
          <div className="hero-actions__buttons">
            <Link to="/signup" className="hero-cta-link">
              <Button className="button--primary-new">
                Start saving up to 15% of billables
              </Button>
            </Link>
            <Button className="button--secondary-new" onClick={onWatchDemo}>
              Watch demo
            </Button>
          </div>
        </div>
      </div>

      <div className="hero-split__right">
        <div
          className={`hero-inbox-card hero-inbox-card--${demoPhase}${demoPaused ? " is-user-paused" : ""}`}
          data-demo-phase={demoPhase}
          onPointerDown={handleCardPointerDown}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") pauseDemo(); }}
        >
          <span
            className={`hero-demo-cursor${CLICK_PHASES.has(demoPhase) ? " is-clicking" : ""}${demoPhase === "email-caselaw-hover" ? " is-hovering" : ""}`}
            style={demoCursorStyle(demoCursorPosition)}
            aria-hidden="true"
          >
            <img src={mouseArrow} alt="" />
          </span>
          <div className="hero-surface-picker" role="tablist" aria-label="Where Vienna works">
            <button
              type="button"
              role="tab"
              aria-selected={surface === "email"}
              className={`hero-surface-tab${surface === "email" ? " is-active" : ""}`}
              onClick={() => { takeOver(); setOpenEmail(null); setSurface("email"); }}
            >
              <span className="hero-surface-tab__logos"><img src={outlookLogo} alt="" /><img src={gmailLogo} alt="" /></span>
              Email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={surface === "collab"}
              className={`hero-surface-tab${surface === "collab" ? " is-active" : ""}`}
              onClick={() => { takeOver(); setSurface("collab"); }}
            >
              <span className="hero-surface-tab__logos"><img src={slackLogo} alt="" /><img src={teamsLogo} alt="" /></span>
              Collab
            </button>
          </div>

          {surface === "email" ? (
            <div className="hero-inbox-window" role="tabpanel" aria-label="Email inbox preview" aria-live="polite">
              {COMPOSE_STAGE_PHASES.has(demoPhase) ? (
                  <div className={`hero-mail-compose-stage${demoPhase === "email-sending" ? " is-sending" : ""}`}>
                  <div className="hero-compose-backdrop" aria-hidden="true">
                    <div className="hero-mail-toolbar">
                      <span className="hero-mail-toolbar__check" />
                      <span className="hero-mail-toolbar__glyph">&#8943;</span>
                      <span className="hero-mail-tabs"><b>Focused</b><span>Other</span></span>
                      <span className="hero-mail-toolbar__filter">Filter &#8964;</span>
                    </div>
                    <ul className="hero-mail-list">
                      {orderedRows.map(([row, index]) => (
                        <li key={row.subject}>
                          <div className={`hero-mail-row${readRows.includes(index) ? " is-read" : ""}`}>
                            <span className="hero-mail-row__unread" aria-hidden="true" />
                            <AgentMark />
                            <span className="hero-mail-row__sender"><strong>Vienna</strong><small>vienna@nougat.law</small></span>
                            <span className="hero-mail-row__body"><strong>{row.subject}</strong><span>{row.preview}</span></span>
                            <span className="hero-mail-row__meta"><time>{rowTime(row, index)}</time><span className={`hero-mail-chip hero-mail-chip--${row.tone}`}>{row.chip}</span></span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`hero-mail-compose${demoPhase === "email-sending" ? " is-sent" : ""}`}>
                  <div className="hero-mail-compose__body">
                    <button type="button" className="hero-mail-compose__close" aria-label="Close email draft" onClick={closeEmailPopout}>&times;</button>
                    <div className="hero-mail-compose__to">To: <strong>vienna@nougat.law</strong></div>
                    <div className="hero-mail-compose__subject">Subject: <strong>{DEMO_SUBJECTS[isNarrow ? MOBILE_EMAIL_INDEXES[demoExample] : demoExample]}</strong></div>
                    <p>{demoRequest}{demoPhase === "email-compose" ? <span className="hero-demo-caret" aria-hidden="true" /> : null}</p>
                    <button type="button" className="hero-mail-compose__send" onClick={sendManualEmail} disabled={demoPhase === "email-sending"}>
                      <span>{demoPhase === "email-sending" ? "Delivered to Vienna" : "Send email"}</span>
                      <TbSend className="hero-mail-compose__send-icon" aria-hidden="true" />
                    </button>
                  </div>
                  </div>
                </div>
              ) : current ? (
                <article key={current.subject} className={`hero-mail-open${demoPhase === "email-caselaw-hover" ? " is-authority-hovering" : ""}`}>
                  <div className="hero-mail-toolbar">
                  <button type="button" className="hero-mail-back" onClick={() => { takeOver(); setOpenEmail(null); }}>&larr; Inbox</button>
                    <span className="hero-mail-toolbar__glyph" aria-hidden="true">&#9633;</span>
                    <span className="hero-mail-toolbar__glyph" aria-hidden="true">&#8943;</span>
                    <span className="hero-mail-toolbar__filter">{openEmail + 1} of {emailRows.length}</span>
                    <button type="button" className="hero-mail-open__close" aria-label="Close email" onClick={closeEmailPopout}>&times;</button>
                  </div>
                  <div className="hero-mail-open__body">
                    <h3>{current.subject}</h3>
                    <div className="hero-mail-open__from">
                      <AgentMark />
                      <span><strong>Vienna</strong> <small>&lt;vienna@nougat.law&gt;</small><br /><small>to Matt K.</small></span>
                      <time>{current.time}</time>
                    </div>
                    {(isNarrow ? current.body.slice(0, 1) : current.body).map((line) => <p key={line}>{line}</p>)}
                    {current.tally ? <Tally tally={current.tally} /> : null}
                    {current.items ? <Findings items={current.items} receipt={current.receipt} /> : null}
                    {current.question ? <Question question={current.question} /> : null}
                    {current.dates ? <DateRail dates={current.dates} /> : null}
                    {current.citations?.length ? (
                      <div className="hero-cite-panel">
                        <div className="hero-cite-panel__heading"><strong>Authorities checked</strong><span>Hover to preview · click to open</span></div>
                        <ol className="hero-cite-list">
                          {(isNarrow ? current.citations.slice(0, 3) : current.citations).map((citation, index) => <CitePreview key={citation.label} citation={citation} index={index} isDemoHovering={demoPhase === "email-caselaw-hover" && index === demoAuthorityIndex} />)}
                        </ol>
                      </div>
                    ) : null}
                    {current.featureCard ? (
                      <HeroFeatureCardVisual
                        card={current.featureCard}
                        documentText={current.documentText}
                        documentTitle={current.documentTitle}
                        fileName={current.fileName}
                      />
                    ) : null}
                    <span className={`hero-mail-chip hero-mail-chip--${current.tone} hero-mail-chip--lg`}>{current.chip}</span>
                    <Actions actions={current.actions} confirm={current.confirm} pressed={emailPressed} />
                  </div>
                </article>
              ) : (
                <>
                  <div className="hero-mail-toolbar" aria-hidden="true">
                    <span className="hero-mail-toolbar__check" />
                    <span className="hero-mail-toolbar__glyph">&#8943;</span>
                    <span className="hero-mail-tabs"><b>Focused</b><span>Other</span></span>
                    <span className="hero-mail-toolbar__filter">Filter &#8964;</span>
                  </div>
                  {demoPhase === "email-delivered" ? (
                    <div className="hero-mail-toast" aria-hidden="true"><TbSend /> Sent to vienna@nougat.law</div>
                  ) : null}
                  <ul className="hero-mail-list">
                    {orderedRows.map(([row, index]) => {
                      const arriving = demoPhase === "email-delivered" && index === demoExample;
                      const makingRoom = demoPhase === "email-delivered" && !arriving;
                      const content = (
                        <>
                          <span className="hero-mail-row__unread" aria-hidden="true" />
                          <AgentMark />
                          <span className="hero-mail-row__sender"><strong>Vienna</strong><small>vienna@nougat.law</small></span>
                          <span className="hero-mail-row__body"><strong>{row.subject}</strong><span>{row.preview}</span></span>
                          <span className="hero-mail-row__meta">
                            <time>{rowTime(row, index)}</time>
                            <span className={`hero-mail-chip hero-mail-chip--${row.tone}`}>{row.chip}</span>
                          </span>
                        </>
                      );
                      return (
                        <li key={row.subject}>
                          <button type="button" className={`hero-mail-row${readRows.includes(index) ? " is-read" : ""}${demoPhase === "email-row-click" && index === demoExample ? " is-demo-target" : ""}${arriving ? " is-arriving" : ""}${makingRoom ? " is-making-room" : ""}`} onClick={() => { takeOver(); markRead(index); setOpenEmail(index); }}>{content}</button>
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="hero-mail-plus" aria-label="New email" onClick={() => { pauseDemo(); setDemoPhase("email-compose"); setDemoExample(0); }}><TbPlus aria-hidden="true" /></button>
                </>
              )}
            </div>
          ) : (
            <div className="hero-inbox-window hero-chat" role="tabpanel" aria-label="Slack and Teams channel preview" aria-live="polite">
              <aside className="hero-chat-sidebar">
                <div className="hero-chat-workspace"><span>KL</span><strong>Kismet Law</strong></div>
                <b>Channels</b>
                {channels.map((name) => (
                  <button
                    type="button"
                    key={name}
                    className={`hero-chat-channel${activeChannel === name ? " is-selected" : ""}${demoPhase === "slack-channel-click" && name === demoChannelTarget ? " is-demo-target" : ""}`}
                    aria-pressed={activeChannel === name}
                    onClick={() => { takeOver(); setChannel(name); }}
                  >
                    # {name}
                  </button>
                ))}
              </aside>
              <div className="hero-chat-main">
                <div className="hero-chat-head">
                  <strong># {activeChannel}</strong>
                  <span>{feed.purpose}</span>
                </div>
                <ChatPressContext.Provider value={slackPressed}>
                <div className="hero-chat-feed">
                  {demoPhase === "slack-channel" ? (
                    <div className="hero-chat-empty">
                      <span className="hero-chat-empty__dot" aria-hidden="true" />
                      <strong>Start a conversation in #{activeChannel}</strong>
                      <span>Send a request to Vienna from this channel.</span>
                    </div>
                  ) : demoPhase === "slack-agent-typing" ? (
                    <>
                      <Msg from="mark" time="now" hideHeader><p><span className="hero-chat-mention">@Vienna</span> {demoRequest}</p></Msg>
                      <div className="hero-agent-reading" aria-label="Vienna is reading">
                        <AgentMark />
                        <span className="hero-agent-reading__lines">
                          <strong>Vienna</strong>
                          {(feed.reading || ["Reading the request", "Checking the matter", "Writing the reply"]).map((line, i) => (
                            <span key={line} className="hero-agent-reading__line" style={{ "--i": i }}>{line}</span>
                          ))}
                        </span>
                        <i aria-hidden="true"><b /> <b /> <b /></i>
                      </div>
                    </>
                  ) : isSlackResponse ? (
                    isNarrow && feed.mobileMessages ? feed.mobileMessages : feed.messages
                  ) : (isSlackCompose || isSlackSend ? null : (isNarrow && feed.mobileMessages ? feed.mobileMessages : feed.messages))}
                </div>
                </ChatPressContext.Provider>
                {isSlackCompose || isSlackSend ? (
                  <div className={`hero-chat-compose${isSlackSend ? " is-sent" : ""}`}>
                    <Msg from="mark" time="now" hideHeader><p><span className="hero-chat-mention">@Vienna</span> {demoRequest}{isSlackCompose ? <span className="hero-demo-caret" aria-hidden="true" /> : null}</p></Msg>
                    {isSlackCompose ? (
                      <button type="button" className="hero-chat-compose__status" onClick={sendManualSlackMessage}>Send to #{activeChannel} <TbSend aria-hidden="true" /></button>
                    ) : <div className="hero-chat-compose__status">Delivered to #{activeChannel} <TbSend aria-hidden="true" /></div>}
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {demoPhase === "email-sending" && planeOrigin ? (
            <span className="hero-mail-plane" style={{ top: `${planeOrigin.top}px`, left: `${planeOrigin.left}px` }} aria-hidden="true"><TbSend /></span>
          ) : null}
        </div>
      </div>
    </section>
  );
};

/* Exposed for the storyboard test so it can follow the same timings. */
export { composeDuration, DEMO_REQUESTS, CLICK_PHASE_MS };

export default HeroSection;
